import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MapPin, Phone } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CONTACT } from "@/data/legal";

export const LegalPage = ({ policy }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [policy?.slug]);

  return (
    <div className="bg-[#FAFAFA] min-h-screen" data-testid={`legal-page-${policy.slug}`}>
      <Header />

      {/* Title band */}
      <section className="bg-[#0A0A0A] text-white border-b border-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="mt-8 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            {policy.title}
          </h1>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-8 text-xs uppercase tracking-[0.15em] text-white/50">
            <span>Effective Date: {policy.effective}</span>
            <span>Last Updated: {policy.updated}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="space-y-5 mb-14">
            {policy.intro.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-[#525252]">
                {p}
              </p>
            ))}
          </div>

          <div className="space-y-12">
            {policy.sections.map((s) => (
              <div key={s.heading} className="border-t border-[#E5E5E5] pt-8">
                <h2 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl text-[#0A0A0A]">
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-base leading-relaxed text-[#525252]">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact block */}
            <div className="border-t border-[#0A0A0A] pt-8">
              <h2 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl text-[#0A0A0A]">
                Contact Us
              </h2>
              <div className="mt-5 space-y-3 text-[#0A0A0A]">
                <div className="font-semibold">{CONTACT.company}</div>
                <div className="flex items-center gap-3 text-sm text-[#525252]">
                  <Mail className="w-4 h-4 text-[#002DFF]" strokeWidth={1.5} />
                  <a href={`mailto:${CONTACT.email}`} className="hover:text-[#002DFF] transition-colors">
                    {CONTACT.email}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#525252]">
                  <Phone className="w-4 h-4 text-[#002DFF]" strokeWidth={1.5} />
                  {CONTACT.phone}
                </div>
                <div className="flex items-start gap-3 text-sm text-[#525252]">
                  <MapPin className="w-4 h-4 text-[#002DFF] mt-0.5" strokeWidth={1.5} />
                  <span>
                    {CONTACT.address.map((line, i) => (
                      <span key={i} className="block">{line}</span>
                    ))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
